// Слой «Покрытие троп маршрутами» (меню «Слои»): полупрозрачные сплошные
// линии всех публичных маршрутов (GET /routes/coverage) и попап по клику со
// списком маршрутов, проходящих в этом месте. Данные грузятся один раз при
// первом включении и переживают пересоздание карты (setup() снова кладёт их
// в новый источник).
import * as maplibregl from "maplibre-gl";
import type { MapCtx } from "../map/mapContext";
import api from "../api";
import { coveragePopupHtml, uniqueRoutesAt, type CoverageRoute } from "../utils/routeCoverage";
import { useRoutesStore } from "../store/routes";
import { gradeAccentColor } from "../utils/routeGrade";
import { formatHikingDuration } from "../utils/formatDuration";

export const ROUTE_COVERAGE_SOURCE = "route-coverage";
export const ROUTE_COVERAGE_LAYER = "route-coverage-line";
// Полуширина зоны клика в пикселях: линия тонкая, на тач-экране в неё
// иначе не попасть.
const HIT_PX = 10;
const EMPTY = { type: "FeatureCollection", features: [] } as const;

export function useRouteCoverage(
  ctx: MapCtx,
  options: { enabled: () => boolean; onSelect: (routeId: number) => void; beforeLayerId?: string }
) {
  let data: object | null = null;
  let loading: Promise<void> | null = null;
  let popup: maplibregl.Popup | null = null;

  const applyVisibility = () => {
    const map = ctx.getMap();
    if (!map?.getLayer(ROUTE_COVERAGE_LAYER)) return;
    map.setLayoutProperty(ROUTE_COVERAGE_LAYER, "visibility", options.enabled() ? "visible" : "none");
    if (!options.enabled()) popup?.remove();
  };

  const pushData = () => {
    const src = ctx.getMap()?.getSource(ROUTE_COVERAGE_SOURCE) as maplibregl.GeoJSONSource | undefined;
    if (src && data) src.setData(data as any);
  };

  const load = () => {
    loading ??= api
      .get("/routes/coverage")
      .then((res) => {
        data = res.data;
        pushData();
      })
      .catch((e) => {
        loading = null;
        ctx.pushDebug("route coverage load failed", String(e));
      });
    return loading;
  };

  // Вызывается из MapView.vue::setupMapLayers - и при первом запуске, и при
  // пересоздании карты после потери WebGL-контекста.
  const setup = (map: maplibregl.Map) => {
    if (map.getSource(ROUTE_COVERAGE_SOURCE)) return;
    map.addSource(ROUTE_COVERAGE_SOURCE, { type: "geojson", data: (data ?? EMPTY) as any });
    const beforeId = options.beforeLayerId && map.getLayer(options.beforeLayerId) ? options.beforeLayerId : undefined;
    map.addLayer(
      {
        id: ROUTE_COVERAGE_LAYER,
        type: "line",
        source: ROUTE_COVERAGE_SOURCE,
        layout: { "line-join": "round", "line-cap": "round", visibility: options.enabled() ? "visible" : "none" },
        paint: {
          "line-color": "#7b3fa8",
          "line-opacity": 0.45,
          "line-width": ["interpolate", ["linear"], ["zoom"], 9, 2, 13, 4, 16, 7],
        },
      },
      beforeId
    );
    if (options.enabled()) void load();
  };

  // Включили/выключили в меню.
  const refresh = () => {
    if (options.enabled()) void load();
    applyVisibility();
  };

  // Из общего обработчика клика карты: true - клик обработан (открыт попап).
  const handleClick = (event: maplibregl.MapMouseEvent): boolean => {
    const map = ctx.getMap();
    if (!map || !options.enabled() || !map.getLayer(ROUTE_COVERAGE_LAYER)) return false;
    const { x, y } = event.point;
    const features = map.queryRenderedFeatures(
      [
        [x - HIT_PX, y - HIT_PX],
        [x + HIT_PX, y + HIT_PX],
      ],
      { layers: [ROUTE_COVERAGE_LAYER] }
    );
    const found = uniqueRoutesAt(features as any);
    if (!found.length) return false;
    // Базовые данные - из каталога маршрутов (если загружен): дистанция,
    // набор, время и цвет сложности.
    const catalog = new Map(useRoutesStore().items.map((r) => [r.id, r]));
    const routes: CoverageRoute[] = found.map(({ id, title }) => {
      const r = catalog.get(id);
      if (!r) return { id, title };
      const time = formatHikingDuration(r.estimated_duration_minutes, ctx.t("units.h"), ctx.t("units.decimal"));
      return {
        id,
        title,
        accent: r.technical_grade ? gradeAccentColor(r.technical_grade) : undefined,
        distance: r.distance_km != null ? `${r.distance_km.toFixed(1).replace(".", ctx.t("units.decimal"))} ${ctx.t("units.km")}` : undefined,
        gain: r.elevation_gain_m != null ? `+${Math.round(r.elevation_gain_m)} ${ctx.t("units.m")}` : undefined,
        time: time || undefined,
      };
    });
    popup?.remove();
    popup = new maplibregl.Popup({ closeButton: true, maxWidth: "300px", className: "coverage-popup-wrap" })
      .setLngLat(event.lngLat)
      .setHTML(coveragePopupHtml(routes, { title: ctx.t("map.layersControl.coveragePopupTitle"), open: ctx.t("map.layersControl.coverageOpen") }))
      .addTo(map);
    popup.getElement().addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>("[data-route-id]");
      if (!btn) return;
      popup?.remove();
      options.onSelect(Number(btn.dataset.routeId));
    });
    return true;
  };

  return { setup, refresh, handleClick, closePopup: () => popup?.remove() };
}
